'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useIdentity } from '@/lib/providers/identity/identity-provider';
import { badgeScopeOf, serialiseBadgeScope } from './badge-scope';
import { NOTIFICATIONS_PAGE, NOTIFICATION_AGGREGATES, NOTIFICATION_FILTER_KEYS, listNotificationsPage, type NotificationFigures } from './api/notifications';
import { useNotificationList } from './notifications-list';
import { useNotificationCentreStore } from './notification-centre-store';
import { useNotificationStore } from './notification-store';
import { toNotificationRow, type NotificationRow } from './notification-row';
import { deriveNotifications, type DerivedNotifications } from '../lib/derive';

/**
 * The centre's SEARCHED page: its own query, its own key, its own figures.
 *
 * WHY IT IS NOT THE TRAY'S READ WITH A FILTER ON IT. The needle was a member of
 * the filter tuple of the ONE read both surfaces mount, and the tray's rows live
 * in a store that `receive` REPLACES, so while a broker typed, the store's whole
 * contents became the matches: the chrome's tray tile, the bell panel, the
 * clients on fire, the carried strip and the settled folds all showed the search
 * result, under a badge that stayed wide because the badge is a set figure. The
 * needle is a property of the view that asked for it, so that view reads its
 * own page and the tray's page is left alone. Plan ruling 17's store stays the
 * TRAY's carrier.
 *
 * WHAT COMES FROM WHERE, once a needle is typed: the rows, the days, the lens
 * counts and the total are this query's, and the bell's three figures stay the
 * tray read's. With no needle this hook is idle and the centre reads the store.
 *
 * THE VERBS PATCH THE STORE, WHICH IS NOT ALWAYS THIS PAGE, and the difference
 * matters. A verb writes through `notification-store.ts`, whose rows are the
 * TRAY's own page, and this query is invalidated with it on the shared
 * `['notifications','centre']` prefix. So:
 *
 * - a searched row that is ALSO in the tray's page is patched at once, and the
 *   bell's figure moves in the same tick, as it does with no search on;
 * - a searched row BEYOND the tray's page is in no store, so the patch finds
 *   nothing to change: the badge delta is nought and neither the row nor the
 *   bell moves until the server answers and this query refetches.
 *
 * That second case is a real gap and it is left open deliberately. Patching
 * this query's cache as well would put an optimistic reading in a place the
 * badge delta cannot see, so the bell and the centre would disagree about the
 * same row in the other direction, and the alternative, giving the searched page
 * its own store, is a second carrier for the same rows against plan ruling 17.
 * A broker searching past the bound waits a round trip; a broker clearing what
 * the bell shows does not, which is the way round that matters.
 *
 * The two queries can disagree about a row for the length of the write plus the
 * refetch, and converge through the family prefix.
 */
export const notificationsSearchQueryKey = ['notifications', 'centre', 'search'] as const;

/**
 * How long the field waits before the query hears about it.
 *
 * EVERY KEYSTROKE IS A ROUND TRIP OVER THE WHOLE SET: a `NOTIFICATIONS_PAGE` page, the
 * set-level total and eight figures, three of which scan the base a second time
 * to rank each thread's face. A typed word is four to eight keystrokes, so
 * without this a word is eight of those. 250ms collapses a word into one
 * request and stays inside the ~300ms that starts to read as lag; the FIELD is
 * never debounced, so the text appears as fast as it is typed.
 */
export const SEARCH_DEBOUNCE_MS = 250;

/** The last settled answer, whole, so one flag switches all of it. */
interface HeldAnswer {
  readonly rows: readonly NotificationRow[];
  readonly figures: NotificationFigures | null;
  readonly truncated: boolean;
  readonly total: number | null;
  readonly hasOlder: boolean;
  readonly atCeiling: boolean;
}
const EMPTY_ANSWER: HeldAnswer = {
  rows: [],
  figures: null,
  truncated: false,
  total: null,
  hasOlder: false,
  atCeiling: false
};
export interface SearchedNotifications extends DerivedNotifications {
  readonly needle: string;
  readonly rows: readonly NotificationRow[];
  readonly figures: NotificationFigures | null;
  readonly truncated: boolean;
  readonly total: number | null;
  readonly loaded: number;
  readonly hasOlder: boolean;
  /** The read's page window is full; see `ListCeilingNotice`. */
  readonly atCeiling: boolean;
  readonly loadingOlder: boolean;
  readonly loadOlder: () => void;
  /**
   * True while the page for THIS needle is in flight.
   *
   * Read by the centre, which says it is looking rather than saying there is
   * nothing to find: with no held page there is nothing to show yet, and
   * "Nothing here matches" under a request that is still out is a wrong answer
   * rather than a slow one.
   */
  readonly searching: boolean;
  /**
   * The read's failure, published rather than swallowed.
   *
   * A failed search rendered "Nothing here matches", which states a negative
   * the browser does not know: the rows it would have matched are exactly what
   * it failed to fetch. It is reachable through any 500 and through this
   * context's own `NOTIFICATIONS_INVALID_BADGE_SCOPE`, whose handler fails the
   * page on purpose rather than serve a count nobody can explain. When it is
   * set the last good page STAYS on screen, because rows that were true a
   * moment ago are better than a sentence that is wrong.
   *
   * It is NOT written into the notification store's `loadError`. That one is
   * the TRAY read's, and a search failing does not mean a broker's
   * notifications did not load.
   */
  readonly failed: boolean;
}

/** The needle the query uses, which lags the field by one interval. */
export const useDebouncedNeedle = () => {
  const $ = _c(4);
  const typed = useNotificationCentreStore(_temp);
  const [needle, setNeedle] = useState(typed);
  if (typed.trim() === "" && needle !== typed) {
    setNeedle(typed);
  }
  let t0;
  let t1;
  if ($[0] !== needle || $[1] !== typed) {
    t0 = () => {
      if (typed === needle || typed.trim() === "") {
        return;
      }
      const timer = setTimeout(() => {
        setNeedle(typed);
      }, SEARCH_DEBOUNCE_MS);
      return () => {
        clearTimeout(timer);
      };
    };
    t1 = [typed, needle];
    $[0] = needle;
    $[1] = typed;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return needle;
};
export const useNotificationSearchResults = () => {
  const $ = _c(39);
  const needle = useDebouncedNeedle();
  const {
    identity
  } = useIdentity();
  const viewerUserId = identity?.userId ?? null;
  const preferences = useNotificationStore(_temp2);
  const lastOpenedAt = useNotificationStore(_temp3);
  let t0;
  if ($[0] !== preferences) {
    t0 = serialiseBadgeScope(badgeScopeOf(preferences));
    $[0] = preferences;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const badgeScope = t0;
  let t1;
  if ($[2] !== needle) {
    t1 = needle.trim();
    $[2] = needle;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const active = t1 !== "";
  let t2;
  if ($[4] !== badgeScope || $[5] !== needle) {
    t2 = {
      includeDone: "true",
      includeSnoozed: "true",
      unreadOnly: "false",
      badgeScope,
      q: needle
    };
    $[4] = badgeScope;
    $[5] = needle;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== active || $[8] !== t2) {
    t3 = {
      keyRoot: notificationsSearchQueryKey,
      sort: "newest",
      filterKeys: NOTIFICATION_FILTER_KEYS,
      filters: t2,
      aggregates: NOTIFICATION_AGGREGATES,
      limit: NOTIFICATIONS_PAGE,
      enabled: active,
      fetchPage: listNotificationsPage
    };
    $[7] = active;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const list = useNotificationList(t3);
  let t4;
  if ($[10] !== list.rows || $[11] !== viewerUserId) {
    let t5;
    if ($[13] !== viewerUserId) {
      t5 = wire => toNotificationRow(wire, viewerUserId);
      $[13] = viewerUserId;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t4 = [...list.rows.map(t5)];
    $[10] = list.rows;
    $[11] = viewerUserId;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const rows = t4;
  const [held, setHeld] = useState(EMPTY_ANSWER);
  let t5;
  if ($[15] !== list.aggregates || $[16] !== list.atCeiling || $[17] !== list.hasNextPage || $[18] !== list.total || $[19] !== list.truncated || $[20] !== rows) {
    t5 = {
      rows,
      figures: list.aggregates,
      truncated: list.truncated,
      total: list.total,
      hasOlder: list.hasNextPage,
      atCeiling: list.atCeiling
    };
    $[15] = list.aggregates;
    $[16] = list.atCeiling;
    $[17] = list.hasNextPage;
    $[18] = list.total;
    $[19] = list.truncated;
    $[20] = rows;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const answer = t5;
  const settled = active && !list.isLoading && list.error === null;
  if (settled && held.rows !== rows) {
    setHeld(answer);
  }
  const failed = active && list.error !== null;
  const waiting = active && list.isLoading;
  const showHeld = waiting || failed;
  const shown = showHeld ? held.rows : answer.rows;
  const figures = showHeld ? held.figures : answer.figures;
  let t6;
  if ($[22] !== lastOpenedAt || $[23] !== preferences || $[24] !== shown) {
    t6 = deriveNotifications(shown, preferences, lastOpenedAt);
    $[22] = lastOpenedAt;
    $[23] = preferences;
    $[24] = shown;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const derived = t6;
  if (!active) {
    return null;
  }
  const t7 = showHeld ? held.truncated : answer.truncated;
  const t8 = showHeld ? held.total : answer.total;
  const t9 = showHeld ? held.hasOlder : answer.hasOlder;
  const t10 = showHeld ? held.atCeiling : answer.atCeiling;
  let t11;
  if ($[26] !== derived || $[27] !== failed || $[28] !== figures || $[29] !== list.fetchNextPage || $[30] !== list.isFetchingNextPage || $[31] !== needle || $[32] !== shown || $[33] !== t10 || $[34] !== t7 || $[35] !== t8 || $[36] !== t9 || $[37] !== waiting) {
    t11 = {
      ...derived,
      needle,
      rows: shown,
      figures,
      truncated: t7,
      total: t8,
      loaded: shown.length,
      hasOlder: t9,
      atCeiling: t10,
      loadingOlder: list.isFetchingNextPage,
      loadOlder: list.fetchNextPage,
      searching: waiting,
      failed
    };
    $[26] = derived;
    $[27] = failed;
    $[28] = figures;
    $[29] = list.fetchNextPage;
    $[30] = list.isFetchingNextPage;
    $[31] = needle;
    $[32] = shown;
    $[33] = t10;
    $[34] = t7;
    $[35] = t8;
    $[36] = t9;
    $[37] = waiting;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  return t11;
};
function _temp(s) {
  return s.search;
}
function _temp2(s) {
  return s.preferences;
}
function _temp3(s_0) {
  return s_0.lastOpenedAt;
}